/**
 * Load Google base functions
 */

(function (pmw, $, undefined) {

	pmw.googleConfigConditionsMet = ({type = null, log = false}) => {

		// Always returns true if Google Consent Mode is active
		if (pmwDataLayer?.pixels?.google?.consent_mode?.is_active) {
			if (log) pmw.console.log("Google Consent Mode is active. Loading the Google pixel.");
			return true;
		}

		// If a type is passed, return true if the user has given consent to that type
		// or false if the user has not given consent to that type.
		if (type) {
			if (!!pmw.consent.categories.get()[type]) {
				if (log) pmw.console.log("The category " + type + " has been approved. Loading the Google pixel.");
				return true;
			} else {
				if (log) pmw.console.log("The category " + type + " has been denied. Not loading the Google pixel.");
				return false;
			}
		}

		// If no type is passed, return true if the user has given consent to the statistics or marketing categories.
		if (!!(pmw.consent.categories.get().marketing || pmw.consent.categories.get().statistics)) {
			if (log) pmw.console.log("The categories statistics and/or marketing are approved. Loading the Google pixel.");
			return true;
		}

		return false;
	};

	pmw.fireGtagGoogleAds = () => {
		try {
			pmwDataLayer.pixels.google.ads.state = "loading";

			if (pmwDataLayer?.pixels?.google?.enhanced_conversions?.is_active) {
				Object.keys(pmwDataLayer.pixels.google.ads.conversion_ids).forEach((key) => {
					gtag("config", key, {"allow_enhanced_conversions": true});
				});
			} else {
				Object.keys(pmwDataLayer.pixels.google.ads.conversion_ids).forEach((key) => {
					gtag("config", key);
				});
			}

			if (pmwDataLayer?.pixels?.google?.ads?.conversion_ids && pmwDataLayer?.pixels?.google?.ads?.phone_conversion_label && pmwDataLayer?.pixels?.google?.ads?.phone_conversion_number) {
				gtag("config", Object.keys(pmwDataLayer.pixels.google.ads.conversion_ids)[0] + "/" + pmwDataLayer.pixels.google.ads.phone_conversion_label, {
					phone_conversion_number: pmwDataLayer.pixels.google.ads.phone_conversion_number,
				});
			}

			pmwDataLayer.pixels.google.ads.state = "ready";
		} catch (e) {
			console.error(e);
		}
	};

	// https://developers.google.com/tag-platform/gtagjs/reference
	pmw.fireGtagGoogleAnalyticsGA4 = () => {

		try {
			pmwDataLayer.pixels.google.analytics.ga4.state = "loading";

			let parameters = pmwDataLayer.pixels.google.analytics.ga4.parameters;

			if (pmwDataLayer?.pixels?.google?.analytics?.ga4?.debug_mode) {
				parameters.debug_mode = true;
			}

			gtag("config", pmwDataLayer.pixels.google.analytics.ga4.measurement_id, parameters);

			pmwDataLayer.pixels.google.analytics.ga4.state = "ready";
		} catch (e) {
			console.error(e);
		}
	};

	/**
	 * Check if Google is active
	 *
	 * @returns {boolean}
	 */
	pmw.isGoogleActive = () => {

		// If GA4 is active, return true
		if (pmwDataLayer?.pixels?.google?.analytics?.ga4?.measurement_id) return true;

		// If Google Ads is active, return true
		if (!jQuery.isEmptyObject(pmwDataLayer?.pixels?.google?.ads?.conversion_ids)) return true;

		return false;
	};

	pmw.getGoogleGtagId = () => {

		if (pmwDataLayer?.pixels?.google?.tag_id) {
			return pmwDataLayer.pixels.google.tag_id;
		}

		if (
			pmwDataLayer?.pixels?.google?.ads?.conversion_ids
			&& Object.keys(pmwDataLayer.pixels.google.ads.conversion_ids)[0]
		) {
			return Object.keys(pmwDataLayer.pixels.google.ads.conversion_ids)[0];
		}

		if (pmwDataLayer?.pixels?.google?.analytics?.ga4?.measurement_id) {
			return pmwDataLayer.pixels.google.analytics.ga4.measurement_id;
		}

		return "";
	};

	/**
	 * Session storage key for caching the GTG handler type
	 */
	const GTG_HANDLER_CACHE_KEY = "gtg_h";

	/**
	 * Flag to prevent multiple detection requests on the same page
	 */
	let gtgDetectionInProgress = false;

	/**
	 * Valid handler types
	 */
	const VALID_HANDLERS = ["external", "standalone", "wordpress"];

	/**
	 * Get the GTG handler from session cache
	 *
	 * Server-side detection is unreliable for Cloudflare (server-to-server requests bypass CDN).
	 * Only use session storage cache from previous browser-side detection.
	 *
	 * @returns {string|null} The handler type or null if not cached
	 */
	const getGtgHandler = () => {
		// Session storage cache from previous detection
		try {
			const cached = pmw.retrieveData(GTG_HANDLER_CACHE_KEY);
			if (cached && VALID_HANDLERS.includes(cached)) {
				return cached;
			}
		} catch (e) {
			// Storage not available
		}

		return null;
	};

	/**
	 * Cache the GTG handler type in _pmw_session_data
	 *
	 * @param {string} handler - The handler type (e.g., "wordpress", "isolated", "external")
	 */
	const cacheGtgHandler = (handler) => {
		try {
			pmw.storeData(GTG_HANDLER_CACHE_KEY, handler);
		} catch (e) {
			// Silently fail if storage is not available
		}
	};

	/**
	 * Detect which GTG handler is responding (synchronous/blocking)
	 *
	 * Makes preflight requests to detect the optimal proxy configuration.
	 *
	 * Detection priority (fastest to slowest):
	 * 1. External proxy (Cloudflare) via measurement_path - fastest
	 * 2. Standalone proxy via proxy_url - fast, bypasses WordPress
	 * 3. WordPress proxy via measurement_path - slowest, full WordPress load
	 *
	 * This function returns a Promise that resolves to the handler type.
	 *
	 * Possible handlers:
	 * - "external": External proxy like Cloudflare (fastest) - use measurement_path
	 * - "standalone": Standalone proxy PHP file (fast, no WordPress) - use proxy_url
	 * - "wordpress": WordPress fallback (slowest) - use measurement_path
	 *
	 * @returns {Promise<string>} The detected handler type
	 */
	const detectGtgHandler = async () => {
		// Prevent multiple detection requests on the same page
		if (gtgDetectionInProgress) {
			// Wait for existing detection to complete
			return new Promise((resolve) => {
				const checkInterval = setInterval(() => {
					const cached = getGtgHandler();
					if (cached) {
						clearInterval(checkInterval);
						resolve(cached);
					}
				}, 10);
				// Timeout after 2 seconds
				setTimeout(() => {
					clearInterval(checkInterval);
					resolve("wordpress");
				}, 2000);
			});
		}
		
		gtgDetectionInProgress = true;

		const tagGateway = pmwDataLayer?.pixels?.google?.tag_gateway;
		
		// Priority 1: Check measurement_path first for Cloudflare (external proxy)
		// Cloudflare won't send X-PMW-GTG-Handler header, so we can detect it
		if (tagGateway?.measurement_path) {
			const healthUrl = tagGateway.measurement_path + "/healthy";
			
			try {
				const response = await fetch(healthUrl, {method: "GET", cache: "no-store"});
				const handler = response.headers.get("X-PMW-GTG-Handler");

				// If no handler header, it's likely Cloudflare (external)
				if (!handler) {
					cacheGtgHandler("external");
					pmw.console.log("GTG handler: external (CDN/edge proxy)");
					return "external";
				}
				
				// If WordPress handler detected on measurement_path, continue to check standalone
				if (handler === "wordpress") {
					pmw.console.log("WordPress proxy detected on measurement_path, checking for standalone proxy...");
					// Fall through to check standalone proxy
				}
				
				// If standalone detected on measurement_path (rare), use it
				if (handler === "standalone") {
					cacheGtgHandler("standalone");
					pmw.console.log("GTG handler: standalone (direct PHP proxy via measurement_path)");
					return "standalone";
				}
			} catch (e) {
				pmw.console.log("measurement_path not accessible:", e.message);
				// Fall through to check isolated proxy
			}
		}
		
		// Priority 2: Check proxy_url for standalone proxy (direct PHP file access)
		// This works when measurement_path goes through WordPress or isn't available
		if (tagGateway?.proxy_url) {
			const healthUrl = tagGateway.proxy_url + "?healthCheck=1";
			try {
				const response = await fetch(healthUrl, {method: "GET", cache: "no-store"});
				const handler = response.headers.get("X-PMW-GTG-Handler");

				if (handler === "standalone") {
					cacheGtgHandler("standalone");
					pmw.console.log("GTG handler: standalone (direct PHP proxy)");
					return "standalone";
				}
			} catch (e) {
				pmw.console.log("proxy_url not accessible:", e.message);
			}
		}
		
		// Priority 3: Fallback to WordPress proxy (via measurement_path)
		cacheGtgHandler("wordpress");
		pmw.console.log("GTG handler: wordpress (WordPress fallback proxy)");
		return "wordpress";
	};

	/**
	 * Get the Google Tag Gateway URL for loading gtag.js
	 *
	 * This is now an async function that waits for handler detection
	 * before returning the optimal URL.
	 *
	 * Priority (fastest to slowest):
	 * - "external" (Cloudflare) -> use measurement_path (fastest)
	 * - "standalone" -> use proxy_url (fast, bypasses WordPress)
	 * - "wordpress" -> use measurement_path (slowest, full WordPress load)
	 *
	 * @returns {Promise<string>} The gateway URL or empty string if not configured.
	 */
	pmw.getGoogleTagGatewayUrlAsync = async () => {

		const tagId = pmw.getGoogleGtagId();
		if (!tagId) {
			return "";
		}

		const tagGateway = pmwDataLayer?.pixels?.google?.tag_gateway;
		if (!tagGateway?.measurement_path) {
			return "";
		}

		// Check server-provided or cached handler first
		let handler = getGtgHandler();
		
		// If no handler available, do async detection
		if (!handler) {
			handler = await detectGtgHandler();
		}

		// External (Cloudflare) - use measurement_path (fastest)
		if (handler === "external") {
			pmw.console.log("Using measurement_path for GTG (external/CDN proxy)");
			return tagGateway.measurement_path + "/?id=" + encodeURIComponent(tagId) + "&s=/gtag/js";
		}
		
		// Standalone proxy - use proxy_url (fast, bypasses WordPress)
		if (handler === "standalone" && tagGateway.proxy_url) {
			pmw.console.log("Using proxy_url for GTG (standalone proxy)");
			return tagGateway.proxy_url + "?id=" + encodeURIComponent(tagId) + "&s=/gtag/js";
		}

		// WordPress fallback - use measurement_path (slowest)
		pmw.console.log("Using measurement_path for GTG (WordPress fallback proxy)");
		return tagGateway.measurement_path + "/?id=" + encodeURIComponent(tagId) + "&s=/gtag/js";
	};

	/**
	 * Get the Google Tag Gateway URL (synchronous version for backward compatibility)
	 * 
	 * Uses server-provided or cached value if available, otherwise falls back to measurement_path.
	 * The async version should be preferred for first page loads.
	 *
	 * @returns {string} The gateway URL or empty string if not configured.
	 */
	getGoogleTagGatewayUrl = () => {

		const tagId = pmw.getGoogleGtagId();
		if (!tagId) {
			return "";
		}

		const tagGateway = pmwDataLayer?.pixels?.google?.tag_gateway;
		if (!tagGateway?.measurement_path) {
			return "";
		}

		// Check server-provided or cached handler to determine which URL to use
		const handler = getGtgHandler();

		// If external (Cloudflare) is detected, always use measurement_path
		if (handler === "external") {
			return tagGateway.measurement_path + "/?id=" + encodeURIComponent(tagId) + "&s=/gtag/js";
		}

		// If isolated detected, use direct proxy URL for best performance
		if (handler === "isolated" && tagGateway.proxy_url) {
			return tagGateway.proxy_url + "?id=" + encodeURIComponent(tagId) + "&s=/gtag/js";
		}

		// Default: use measurement_path (for WordPress fallback or unknown handler)
		return tagGateway.measurement_path + "/?id=" + encodeURIComponent(tagId) + "&s=/gtag/js";
	};

	/**
	 * Get the Google tracking URL (async version)
	 * 
	 * @returns {Promise<string>} The tracking URL
	 */
	pmw.getGoogleTrackingUrlAsync = async () => {

		// Skip the gateway proxy for bot user agents to reduce server load
		// Bots will use the standard googletagmanager.com URL directly
		if (pmw.isBotUserAgent()) {
			return getGoogleStandardTrackingUrl();
		}

		// Try to use the Google Tag Gateway (local proxy to FPS)
		const gatewayUrl = await pmw.getGoogleTagGatewayUrlAsync();
		if (gatewayUrl) {
			return gatewayUrl;
		}

		return getGoogleStandardTrackingUrl();
	};

	getGoogleTrackingUrl = () => {

		// Skip the gateway proxy for bot user agents to reduce server load
		// Bots will use the standard googletagmanager.com URL directly
		if (pmw.isBotUserAgent()) {
			return getGoogleStandardTrackingUrl();
		}

		// Try to use the Google Tag Gateway (local proxy to FPS)
		const gatewayUrl = getGoogleTagGatewayUrl();
		if (gatewayUrl) {
			return gatewayUrl;
		}

		return getGoogleStandardTrackingUrl();
	};

	getGoogleStandardTrackingUrl = () => {
		return "https://www.googletagmanager.com/gtag/js?id=" + pmw.getGoogleGtagId();
	}

	pmw.loadGoogle = async () => {

		if (!pmw.isGoogleActive()) {
			return;
		}

		pmwDataLayer.pixels.google.state = "loading";

		// Wait for GTG handler detection before determining the URL
		// This ensures we use the correct proxy URL even on first page load
		const trackingUrl = await pmw.getGoogleTrackingUrlAsync();

		pmw.console.log("Loading Google gtag.js from", trackingUrl.startsWith("/") ? window.location.origin + trackingUrl : trackingUrl);

		pmw.loadScriptAndCacheIt(trackingUrl, getGoogleStandardTrackingUrl())
			.then((script, textStatus) => {

				try {

					// If Google TCF is active, output the TCF script
					// https://developers.google.com/tag-platform/security/guides/implement-TCF-strings
					if (pmwDataLayer?.pixels?.google?.tcf_support) {
						window["gtag_enable_tcf_support"] = true;
					}

					// Initiate Google dataLayer and gtag
					window.dataLayer = window.dataLayer || [];

					window.gtag = function gtag() {

						pmw.console.log("gtag called with", arguments);
						pmw.consent.api.processExternalGcmConsentUpdate_experimental(arguments);

						// Delete the source property from the arguments object
						// It is a helper property that is not needed by gtag for regular calls
						if (arguments.length === 3 && arguments[2]?.source) {
							delete arguments[2].source;
						}

						dataLayer.push(arguments);
					};

					// Google Consent Mode
					if (pmwDataLayer?.pixels?.google?.consent_mode?.is_active) {

						let google_consent_settings = {
							ad_personalization     : pmw.consent.categories.get().marketing ? "granted" : "denied",
							ad_storage             : pmw.consent.categories.get().marketing ? "granted" : "denied",
							ad_user_data           : pmw.consent.categories.get().marketing ? "granted" : "denied",
							analytics_storage      : pmw.consent.categories.get().statistics ? "granted" : "denied",
							functionality_storage  : pmw.consent.categories.get().preferences ? "granted" : "denied",
							personalization_storage: pmw.consent.categories.get().preferences ? "granted" : "denied",
							security_storage       : pmw.consent.categories.get().necessary ? "granted" : "denied",
							wait_for_update        : pmwDataLayer.pixels.google.consent_mode.wait_for_update,
						};

						if (pmwDataLayer?.general?.consent_management?.restricted_regions) {
							google_consent_settings.region = pmwDataLayer.general.consent_management.restricted_regions;
						}

						pmw.console.log("Google Consent Mode settings", google_consent_settings);

						gtag("consent", "default", google_consent_settings);
						gtag("set", "ads_data_redaction", pmwDataLayer.pixels.google.consent_mode.ads_data_redaction);
						gtag("set", "url_passthrough", pmwDataLayer.pixels.google.consent_mode.url_passthrough);
					}

					// Google Linker
					// https://developers.google.com/gtagjs/devguide/linker
					if (pmwDataLayer?.pixels?.google?.linker?.settings) {
						gtag("set", "linker", pmwDataLayer.pixels.google.linker.settings);
					}

					gtag("js", new Date());
					gtag("set", "developer_id.dNDI5Yz", true);

					/**
					 * Set Enhanced Conversion Data for Google Ads and GA4 on the order received page
					 *
					 * https://support.google.com/google-ads/answer/9888145#zippy=%2Cvalidate-your-implementation-using-chrome-developer-tools
					 * GA4: https://support.google.com/analytics/answer/14171598?sjid=9970117052450433556-EU
					 */
					if (
						pmwDataLayer?.shop?.page_type
						&& "order_received_page" === pmwDataLayer.shop.page_type
						&& pmwDataLayer?.order?.google?.enhanced_conversion_data
					) {
						gtag("set", "user_data", pmwDataLayer.order.google.enhanced_conversion_data);
					}

					// Google Ads loader
					if (!jQuery.isEmptyObject(pmwDataLayer?.pixels?.google?.ads?.conversion_ids)) {  // Only run if the pixel has set up
						if (pmw.googleConfigConditionsMet({type: "marketing"})) {  							// Only run if cookie consent has been given
							pmw.fireGtagGoogleAds();
						} else {
							pmw.consent.logSuppressedPixel("marketing", "Google Ads");
						}
					}

					// GA4 loader
					if (pmwDataLayer?.pixels?.google?.analytics?.ga4?.measurement_id) {  			// Only run if the pixel has set up

						if (pmw.googleConfigConditionsMet({type: "statistics"})) {						// Only run if cookie consent has been given
							pmw.fireGtagGoogleAnalyticsGA4();
						} else {
							pmw.consent.logSuppressedPixel("statistics", "GA4");
						}
					}

					pmwDataLayer.pixels.google.state = "ready";

				} catch (e) {
					console.error(e);
				}
			})
			.catch((jqXHR, textStatus, errorThrown) => {
				pmw.console.error("The URL " + trackingUrl + " could not be loaded. Error: " + errorThrown);
				pmwDataLayer.pixels.google.state = "error";
			});
	};

	pmw.gtagLoaded = () => {
		return new Promise((resolve, reject) => {

			// If Google is not active, reject the promise
			if (!pmw.isGoogleActive()) reject("Google is not active");

			let startTime = 0;
			let timeout   = 5000;
			let frequency = 200;

			(function wait() {
				if (pmwDataLayer?.pixels?.google?.state === "ready") return resolve();
				if (startTime >= timeout) {
					pmw.console.error("Google gtag failed to load. Probably a third party script is blocking it.");
					return reject();
				}
				startTime += frequency;
				setTimeout(wait, frequency);
			})();
		});
	};

	/**
	 * Update Google Consent Mode
	 *
	 * @param statistics
	 * @param marketing
	 * @param preferences
	 * @param necessary
	 */
	pmw.updateGoogleConsentMode = ({
									   statistics = pmw.consent.categories.get().statistics,
									   marketing = pmw.consent.categories.get().marketing,
									   preferences = pmw.consent.categories.get().preferences,
									   necessary = pmw.consent.categories.get().necessary,
								   }) => {

		try {
			if (!window.gtag) return;

			let data = {
				analytics_storage      : statistics ? "granted" : "denied",
				ad_storage             : marketing ? "granted" : "denied",
				ad_user_data           : marketing ? "granted" : "denied",
				ad_personalization     : marketing ? "granted" : "denied",
				functionality_storage  : preferences ? "granted" : "denied",
				personalization_storage: preferences ? "granted" : "denied",
				security_storage       : necessary ? "granted" : "denied",
				source                 : "pmw",
			};

			gtag("consent", "update", data);
			pmw.console.log("Google Consent Mode updated", data);
		} catch (e) {
			console.error(e);
		}
	};


}(window.pmw = window.pmw || {}, jQuery));
